import {
  FAQ_TOPICS,
  FALLBACK_FAQS,
  FALLBACK_SECTIONS,
  INFO_PAGE_META,
  sectionsToHtml,
} from './alfa9-info-content.js';
import { BN_FAQS, BN_INFO_TITLES, BN_SECTIONS } from '../i18n/info-bn.js';
import { createPlayerI18n } from '../i18n/language.js';

/**
 * Reference-matched informational pages (bdvegasguide visual language)
 * rendered inside the Alfa9 guest shell (header / rail / footer / guest bar).
 */
export function createAlfa9InfoPage({ useState, useEffect, jsx, jsxs }) {
  const usePlayerI18n = createPlayerI18n({ useState, useEffect });
  function openLiveChat() {
    try {
      const btn = document.querySelector('.ninet6-root .livechat');
      if (btn) btn.click();
    } catch {
      /* ignore */
    }
  }

  function Alfa9InfoPage({
    pageKey,
    onClose,
    loadRules,
    loadFaqs,
    telegramUrl = '',
    navigate,
  }) {
    function go(path) {
      if (typeof navigate === 'function') navigate(path);
      else if (typeof window !== 'undefined') window.location.assign(path);
    }

    const { lang, phrase } = usePlayerI18n();
    const meta = INFO_PAGE_META[pageKey] || { title: 'Information', type: pageKey };
    const pageTitle = lang === 'bn' ? (BN_INFO_TITLES[pageKey] || phrase(meta.title)) : meta.title;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [apiDoc, setApiDoc] = useState(null);
    const [faqs, setFaqs] = useState([]);
    const [query, setQuery] = useState('');
    const [topic, setTopic] = useState('all');

    useEffect(() => {
      const ac = new AbortController();
      setLoading(true);
      setError('');
      setApiDoc(null);
      setFaqs([]);

      const apiRuleTypes = new Set([
        'responsible_gaming',
        'about_us',
        'security',
        'privacy_policy',
        'terms_conditions',
        'age_restriction',
        'kyc',
        'aml',
      ]);

      let run;
      if (meta.type === 'faq') {
        run = Promise.resolve(typeof loadFaqs === 'function' ? loadFaqs({ signal: ac.signal }) : [])
          .then((rows) => {
            const list = Array.isArray(rows) ? rows : [];
            setFaqs(list.length ? list : FALLBACK_FAQS);
          })
          .catch(() => {
            if (!ac.signal.aborted) setFaqs(FALLBACK_FAQS);
          });
      } else if (meta.type === 'contact_us' || meta.type === 'affiliate' || !apiRuleTypes.has(meta.type)) {
        run = Promise.resolve();
      } else {
        run = Promise.resolve(typeof loadRules === 'function' ? loadRules(meta.type, { signal: ac.signal }) : null)
          .then((doc) => {
            setApiDoc(doc && (doc.content || doc.title) ? doc : null);
          })
          .catch(() => {
            if (!ac.signal.aborted) setApiDoc(null);
          });
      }

      run.finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });

      return () => ac.abort();
    }, [meta.type, loadRules, loadFaqs]);

    useEffect(() => {
      try {
        window.scrollTo(0, 0);
      } catch {
        /* ignore */
      }
    }, [meta.type]);

    const fallbackHtml = sectionsToHtml((lang === 'bn' ? BN_SECTIONS : FALLBACK_SECTIONS)[meta.type] || []);
    const title = apiDoc?.title || pageTitle;
    const hasApiHtml = Boolean(apiDoc?.content);
    const isFaq = meta.type === 'faq';

    const filteredFaqs = (faqs || []).filter((item) => {
      const local = lang === 'bn' ? BN_FAQS[item.id] : null;
      const q = String(local?.question || item.question || '');
      const a = String(local?.answer || item.answer || '').replace(/<[^>]+>/g, ' ');
      const hay = `${q} ${a}`.toLowerCase();
      const matchQuery = !query.trim() || hay.includes(query.trim().toLowerCase());
      const itemTopic = String(item.topic || item.category || 'account').toLowerCase();
      const matchTopic = topic === 'all' || itemTopic === topic;
      return matchQuery && matchTopic;
    });

    return jsxs('section', {
      className: `a9-info-page${isFaq ? ' is-faq' : ''}`,
      'aria-label': title,
      children: [
        jsxs('div', {
          className: 'a9-info-topbar',
          children: [
            jsx('button', {
              type: 'button',
              className: 'a9-info-back',
              onClick: onClose,
              'aria-label': phrase('Back to home'),
              children: phrase('← Home'),
            }),
            jsx('div', { className: 'a9-info-topbar-title', children: pageTitle }),
          ],
        }),
        jsxs('div', {
          className: 'a9-info-container',
          children: [
            loading && jsx('p', { className: 'a9-info-muted', children: phrase('Loading…') }),
            !loading && error && jsx('p', { className: 'a9-info-error', children: error }),
            !loading &&
              !error &&
              isFaq &&
              jsxs('div', {
                className: 'a9-info-faq',
                children: [
                  jsx('div', { className: 'a9-info-faq-star', 'aria-hidden': 'true', children: '✦' }),
                  jsx('h2', { className: 'a9-info-h2 a9-info-faq-title', children: phrase('Frequently Asked Questions') }),
                  jsx('p', {
                    className: 'a9-info-faq-lead',
                    children: phrase('Here are some common questions and answers about Casino.'),
                  }),
                  jsx('p', {
                    className: 'a9-info-faq-lead',
                    children: phrase(
                      'If you cannot find your question in the list below, please contact our customer support through Live Chat.',
                    ),
                  }),
                  jsxs('div', {
                    className: 'a9-info-search',
                    children: [
                      jsx('input', {
                        type: 'search',
                        className: 'a9-info-search-input',
                        placeholder: phrase('Search your questions here...'),
                        value: query,
                        onChange: (e) => setQuery(e.target.value),
                        'aria-label': phrase('Search FAQs'),
                      }),
                      jsx('button', {
                        type: 'button',
                        className: 'a9-info-search-btn',
                        onClick: () => {},
                        children: phrase('Search'),
                      }),
                    ],
                  }),
                  jsx('h2', { className: 'a9-info-h2', children: phrase('Explore Help Topics') }),
                  jsxs('div', {
                    className: 'a9-info-topics',
                    children: [
                      jsx(
                        'button',
                        {
                          type: 'button',
                          className: `a9-info-topic${topic === 'all' ? ' is-active' : ''}`,
                          onClick: () => setTopic('all'),
                          children: jsx('span', { className: 'a9-info-topic-label', children: phrase('All') }),
                        },
                        'all',
                      ),
                      ...FAQ_TOPICS.map((t) =>
                        jsx(
                          'button',
                          {
                            type: 'button',
                            className: `a9-info-topic${topic === t.id ? ' is-active' : ''}`,
                            onClick: () => setTopic(t.id),
                            children: jsx('span', { className: 'a9-info-topic-label', children: phrase(t.label) }),
                          },
                          t.id,
                        ),
                      ),
                    ],
                  }),
                  filteredFaqs.length === 0
                    ? jsx('p', { className: 'a9-info-muted', children: phrase('No matching questions found.') })
                    : jsx('div', {
                        className: 'a9-info-faq-list',
                        children: filteredFaqs.map((item) => {
                          const local = lang === 'bn' ? BN_FAQS[item.id] : null;
                          const question = local?.question || item.question;
                          const answer = local?.answer || item.answer || '';
                          return jsxs(
                            'details',
                            {
                              className: 'a9-info-faq-item',
                              children: [
                                jsx('summary', { children: question }),
                                jsx('div', {
                                  className: 'a9-info-faq-answer',
                                  dangerouslySetInnerHTML: { __html: answer },
                                }),
                              ],
                            },
                            item.id || question,
                          );
                        }),
                      }),
                  jsx('button', {
                    type: 'button',
                    className: 'a9-info-chat-btn',
                    onClick: openLiveChat,
                    children: phrase('Live Chat'),
                  }),
                ],
              }),
            !loading &&
              !error &&
              meta.type === 'contact_us' &&
              jsxs('div', {
                className: 'a9-info-body',
                children: [
                  jsx('div', {
                    className: 'a9-info-rich',
                    dangerouslySetInnerHTML: { __html: fallbackHtml },
                  }),
                  jsxs('div', {
                    className: 'a9-info-support-card',
                    children: [
                      jsx('h3', { children: phrase('Customer Support') }),
                      jsx('p', { children: phrase('Live Chat: Available on the Casino website for quick help.') }),
                      telegramUrl
                        ? jsxs('p', {
                            children: [
                              phrase('Telegram: '),
                              jsx('a', {
                                href: telegramUrl,
                                target: '_blank',
                                rel: 'noopener noreferrer',
                                children: telegramUrl,
                              }),
                            ],
                          })
                        : jsx('p', {
                            children:
                              phrase('Additional contact channels will appear here when officially published for Casino.'),
                          }),
                      jsx('button', {
                        type: 'button',
                        className: 'a9-info-chat-btn',
                        onClick: openLiveChat,
                        children: phrase('Open Live Chat'),
                      }),
                    ],
                  }),
                ],
              }),
            !loading &&
              !error &&
              meta.type !== 'faq' &&
              meta.type !== 'contact_us' &&
              jsxs('div', {
                className: 'a9-info-body',
                children: [
                  hasApiHtml
                    ? jsxs('div', {
                        children: [
                          jsx('h2', { className: 'a9-info-h2', children: title }),
                          jsx('div', {
                            className: 'a9-info-rich',
                            dangerouslySetInnerHTML: { __html: apiDoc.content },
                          }),
                        ],
                      })
                    : jsx('div', {
                        className: 'a9-info-rich',
                        dangerouslySetInnerHTML: {
                          __html: fallbackHtml || `<h2>${title}</h2><p>${phrase('Content is not available yet.')}</p>`,
                        },
                      }),
                  meta.type === 'affiliate'
                    ? jsxs('div', {
                        className: 'a9-info-contact-actions',
                        children: [
                          jsx('button', {
                            type: 'button',
                            className: 'a9-info-chat-btn',
                            onClick: () => go('/signup'),
                            children: phrase('Sign Up'),
                          }),
                          jsx('button', {
                            type: 'button',
                            className: 'a9-info-ext-link',
                            onClick: () => go('/login'),
                            children: phrase('Login'),
                          }),
                          jsx('button', {
                            type: 'button',
                            className: 'a9-info-ext-link',
                            onClick: () => go('/profile/referral'),
                            children: phrase('Referral Tools'),
                          }),
                        ],
                      })
                    : null,
                ],
              }),
          ],
        }),
      ],
    });
  }

  return { Alfa9InfoPage };
}
